// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the research record's inlet (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`,
 * slice 4): the research tools say what they computed or exported, and
 * the session recorder (`researchRecord.ts`), when one is recording, takes
 * it down. Nothing is kept here and nothing leaves the browser: with no
 * recorder listening, every call is a no-op, and `trailOn()` lets a tool
 * skip building what nobody will read.
 *
 * Numbers are passed at full precision, in the dataset's units, with
 * English method names, like the CSV files.
 */

export type TrailType = 'stats' | 'baseline' | 'change' | 'analysis' | 'compare' | 'hovmoller' | 'cite' | 'figure' | 'methods' | 'threshold' | 'regionSeries'

export interface TrailEntry {
  readonly type: TrailType
  readonly data: Record<string, unknown>
  /**
   * Entries with the same key replace each other instead of piling up: a
   * statistic recomputed for the same region on the same view, a trend
   * that sharpens as playback fills the series.
   */
  readonly coalesce?: string
  /** The numbers describe the frame on screen (statistics), not a span of frames (a trend). */
  readonly frame?: boolean
  /**
   * AYNI (owner, 2026-10-07): who did it, when not the person at the
   * screen: `assistant` for a number the AYNI chatbot computed by command
   * (never its chat text), written to the record as "via the chatbot".
   */
  readonly actor?: TrailActor
}

export type TrailActor = 'assistant'

export interface TrailFile {
  /** The file name the browser saved it as. */
  readonly name: string
  readonly body: string | Blob
  /** `csv`, `png`, `citation`… */
  readonly kind: string
}

export interface TrailSink {
  entry(e: TrailEntry): void
  file(f: TrailFile): void
}

let sink: TrailSink | null = null

/** The recorder plugs in while it records, and out when it stops. */
export function setTrailSink(next: TrailSink | null): void {
  sink = next
}

/** Whether anything is recording. */
export function trailOn(): boolean {
  return sink !== null
}

export function trail(entry: TrailEntry): void {
  if (!sink) return
  sink.entry(acting && !entry.actor ? { ...entry, actor: acting } : entry)
}

let acting: TrailActor | null = null

/** AYNI: whether the chatbot is running a command now (a tool that will keep noting later can remember it). */
export function assistantActing(): boolean {
  return acting === 'assistant'
}

/**
 * AYNI: run `fn` as the chatbot: every entry the research tools note while
 * it runs (synchronously) is the chatbot's. The chatbot never starts a
 * record: with none recording this changes nothing.
 */
export function asAssistant<T>(fn: () => T): T {
  const before = acting
  acting = 'assistant'
  try {
    return fn()
  } finally {
    acting = before
  }
}

/** A file the user exported: its name and bytes, so the record can hold its SHA-256. */
export function trailFile(file: TrailFile): void {
  sink?.file(file)
}
